/**
 * The browser downloader's engine: fetch a chunk's files, zip them, hand the zip to the
 * browser, tell the server what landed. See todo/browser-downloader.md.
 *
 * Deliberately has no DOM dependencies beyond the anchor click, so the queue can later move
 * into a Web Worker without restructuring — background tabs throttle timers hard, and these
 * downloads run for tens of minutes.
 *
 * The old Lambda downloader is untouched; this only runs for members with
 * `uses_browser_downloader` set.
 */
import { downloadZip } from "client-zip";
import { api } from "../../api";

/** Parallel fetches. Measured against `/mockup-render` on 2026-09-02: latency stays flat at
 *  ~0.85 s from 1 to 10 concurrent (1.2 → 9.8 requests/s), so 10 extracts nearly all the
 *  available throughput without hammering one origin. */
const CONCURRENCY = 10;

/** Retries per file. A mockup render is a Lambda invocation and can cold-start; a single
 *  blip should not cost the reseller a whole chunk. */
const MAX_ATTEMPTS = 3;

export interface DownloadFile {
  artworkId: number;
  target: string;
  url: string;
}

export interface ChunkProgress {
  /** Files fetched so far in this chunk. */
  filesDone: number;
  /** Files in this chunk. */
  filesTotal: number;
  /** Artworks reported delivered across the whole job. */
  artworksDone: number;
  /** Artworks in the whole job. */
  artworksTotal: number;
  /** 1-based chunk being worked on. */
  chunkIndex: number;
  chunkCount: number;
  /** Files that could not be fetched after every retry. */
  failures: string[];
}

/**
 * One zip's worth of work, as the server planned it.
 *
 * `partIndex` and `filename` come from the server rather than the loop counter: they are the
 * identity the reseller sees ("part 3", `…-part3.zip`) and the key a later re-download is
 * requested by, so a resumed job — which skips finished parts and therefore has a different
 * loop counter — must not renumber them.
 */
export interface DownloadPart {
  partIndex: number;
  artworkIds: number[];
  filename: string;
}

export interface EngineCallbacks {
  onProgress: (p: ChunkProgress) => void;
  onDone: (summary: { artworksDone: number; artworksTotal: number; failures: string[] }) => void;
  onError: (message: string) => void;
}

/** Fetch one file, retrying transient failures. Returns null when it cannot be had. */
async function fetchFile(file: DownloadFile, signal: AbortSignal): Promise<Blob | null> {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      // Credentials per origin, and this matters twice over.
      //
      // Our own endpoints (`/mockup-render`, `/csv-dl`) are session-authenticated and need
      // the cookie. S3 presigned URLs carry their own signature and must NOT get one: a
      // credentialed cross-origin request requires the bucket to echo an explicit origin AND
      // `Access-Control-Allow-Credentials`, which a wildcard CORS policy cannot do — so
      // sending cookies would fail even against a correctly configured bucket.
      const sameOrigin = file.url.startsWith("/") || file.url.startsWith(location.origin);
      const res = await fetch(file.url, {
        signal,
        credentials: sameOrigin ? "same-origin" : "omit",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.blob();
    } catch {
      // The error itself carries nothing actionable — every failure mode here (network blip,
      // cold-start timeout, 5xx from a render) is handled the same way: retry, then give up
      // and let the caller record the filename. An abort is the user's decision, not a
      // failure, so it stops immediately rather than retrying.
      if (signal.aborted) return null;
      if (attempt === MAX_ATTEMPTS) return null;
      // Linear backoff; a cold Lambda needs a moment, not a thundering herd.
      await new Promise((r) => setTimeout(r, 500 * attempt));
    }
  }
  return null;
}

/** What a chunk's pass produced, filled in as the generator runs. */
interface ChunkTally {
  /** Files fetched so far, successes and failures alike. */
  done: number;
  /** Names of files that could not be had after every retry. */
  failures: string[];
  /** Artworks whose ORIGINAL landed — the only thing that counts as delivered. */
  delivered: Set<number>;
  /** Files actually written into the zip. */
  zipped: number;
}

/**
 * Fetch a chunk's files with bounded look-ahead, yielding them IN ORDER for `client-zip` to
 * consume as it writes the archive.
 *
 * This is what keeps memory flat on large jobs. The previous version fetched the whole chunk
 * into an array of blobs and only then built the zip, so peak usage was the chunk twice over
 * — every input still referenced while the entire output was assembled. Streaming means a
 * blob is dropped as soon as it has been written, and at most CONCURRENCY of them are alive
 * at once, whatever the chunk size.
 *
 * Order is preserved because the window is drained FIFO by index: a reseller comparing two
 * downloads sees the same layout, and it costs nothing — a slow file simply holds its slot
 * while the ones behind it are already in flight.
 */
async function* prefetchFiles(
  files: DownloadFile[],
  signal: AbortSignal,
  tally: ChunkTally,
  onFile: () => void
): AsyncGenerator<{ name: string; input: Blob }> {
  const window: { file: DownloadFile; blob: Promise<Blob | null> }[] = [];
  let next = 0;

  const fill = (): void => {
    while (window.length < CONCURRENCY && next < files.length) {
      const file = files[next++]!;
      window.push({ file, blob: fetchFile(file, signal) });
    }
  };
  fill();

  while (window.length > 0) {
    const entry = window.shift()!;
    const blob = await entry.blob;
    tally.done++;
    onFile();
    // Top the window back up only after one has been consumed, so look-ahead stays bounded.
    fill();
    if (signal.aborted) return;

    if (!blob) {
      tally.failures.push(entry.file.target);
      continue;
    }
    // A failed mockup must not withhold the artwork — the original carries the licence.
    if (entry.file.target === `${entry.file.artworkId}.jpg`) {
      tally.delivered.add(entry.file.artworkId);
    }
    tally.zipped++;
    yield { name: entry.file.target, input: blob };
  }
}

/**
 * Hand a finished zip to the browser, returning its object URL so the caller can release it.
 *
 * Revoking is the caller's job because the safe moment is when the NEXT zip is handed over:
 * revoking synchronously can cancel a download that has not started reading yet, while the
 * old fixed 60 s timer meant several multi-hundred-MB zips could be retained at once on a
 * job whose chunks finish quickly. Releasing the previous one gives it a whole chunk's grace
 * and still caps retention at a single archive.
 */
function saveZip(blob: Blob, filename: string): string {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return url;
}

/** Grace period before releasing the LAST zip, which has no successor to trigger it. */
const FINAL_ZIP_RELEASE_MS = 30_000;

/**
 * Run a browser download to completion (or until aborted).
 *
 * One zip per chunk. A partial zip is corrupt — the central directory is written last — so a
 * chunk is only reported once its zip has actually been handed to the browser. Reporting
 * earlier would mark artworks delivered that the reseller never received; reporting late at
 * worst re-downloads one chunk.
 */
export async function runBrowserDownload(
  args: {
    downloadId: string;
    parts: DownloadPart[];
    artworksTotal: number;
    artworksAlreadyDone: number;
    signal: AbortSignal;
  },
  cb: EngineCallbacks
): Promise<void> {
  const { downloadId, parts, signal } = args;
  let artworksDone = args.artworksAlreadyDone;
  const failures: string[] = [];
  /** Object URL of the zip handed over most recently; released when the next one lands. */
  let previousZipUrl: string | null = null;

  try {
    for (let c = 0; c < parts.length; c++) {
      if (signal.aborted) break;
      const part = parts[c]!;

      const manifest = await api.downloadCart.browserChunkManifest({
        downloadId,
        artworkIds: part.artworkIds,
      });
      // Everything in this chunk was already delivered (a resumed job, or a repeated report).
      if (manifest.files.length === 0) continue;

      const tally: ChunkTally = { done: 0, failures: [], delivered: new Set(), zipped: 0 };
      const report = (): void =>
        cb.onProgress({
          filesDone: tally.done,
          filesTotal: manifest.files.length,
          // Artworks confirmed by the server PLUS those already landed in the chunk being
          // fetched. Counting only confirmed chunks left the display on "0 of 4" for the
          // whole of a single-chunk download: the one report arrives as the overlay closes,
          // so the finished count is never on screen. Same mistake the progress bar made.
          artworksDone: artworksDone + tally.delivered.size,
          artworksTotal: args.artworksTotal,
          chunkIndex: c + 1,
          chunkCount: parts.length,
          failures: [...failures, ...tally.failures],
        });
      report();

      // The zip is built as the files arrive rather than after they have all landed, so only
      // the archive itself and the small look-ahead window are ever in memory.
      const zip = await downloadZip(
        prefetchFiles(manifest.files, signal, tally, report)
      ).blob();
      failures.push(...tally.failures);
      if (signal.aborted) break;
      if (tally.zipped === 0) continue;

      const url = saveZip(zip, part.filename);
      if (previousZipUrl) URL.revokeObjectURL(previousZipUrl);
      previousZipUrl = url;

      const delivered = [...tally.delivered];
      if (delivered.length > 0) {
        const res = await api.downloadCart.reportBrowserChunk({
          downloadId,
          artworkIds: delivered,
          // The stored part, so the downloads modal can show which zips actually landed and
          // offer the others for re-download.
          partIndex: part.partIndex,
        });
        artworksDone = res.completed;
        // These are now counted in `artworksDone`; leaving them in the tally would count the
        // chunk twice on the report that follows.
        tally.delivered.clear();
      }
      report();
    }

    cb.onDone({ artworksDone, artworksTotal: args.artworksTotal, failures });
  } catch (e) {
    if (signal.aborted) {
      cb.onDone({ artworksDone, artworksTotal: args.artworksTotal, failures });
      return;
    }
    cb.onError(e instanceof Error ? e.message : "Download failed");
  } finally {
    // The last zip has no successor to release it, so it gets a grace period instead — long
    // enough for the browser to have taken the bytes, short enough not to strand an archive
    // in memory for the rest of the session.
    if (previousZipUrl) {
      const url = previousZipUrl;
      previousZipUrl = null;
      setTimeout(() => URL.revokeObjectURL(url), FINAL_ZIP_RELEASE_MS);
    }
  }
}

/**
 * Rebuild ONE already-delivered zip and hand it to the browser again.
 *
 * The browser downloader keeps no copy of what it built — the zip goes straight to the OS —
 * so "download part 3 again" means fetching part 3's files and re-assembling them. That is
 * the whole feature: a zip that failed to save can be replaced without re-running the job
 * and without the other parts arriving a second time.
 *
 * It reports NOTHING to the server. The files were already delivered and already counted;
 * `reportBrowserChunk` is idempotent so a report would be harmless today, but calling it
 * here is how a rebuild would start moving job progress the day that changes — which is the
 * failure the Lambda downloader shipped with (progress reaching 142%,
 * todo/downloader-hardening.md §2).
 */
export async function rebuildDownloadPart(
  args: { files: DownloadFile[]; filename: string; signal: AbortSignal },
  cb: { onProgress: (done: number, total: number) => void; onDone: (failures: string[]) => void }
): Promise<void> {
  const tally: ChunkTally = { done: 0, failures: [], delivered: new Set(), zipped: 0 };
  const report = (): void => cb.onProgress(tally.done, args.files.length);
  report();

  const zip = await downloadZip(prefetchFiles(args.files, args.signal, tally, report)).blob();
  if (args.signal.aborted) {
    cb.onDone(tally.failures);
    return;
  }

  const url = saveZip(zip, args.filename);
  setTimeout(() => URL.revokeObjectURL(url), FINAL_ZIP_RELEASE_MS);
  cb.onDone(tally.failures);
}
